'use client';

import { useLocale } from 'next-intl';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { getYourImaiCopy, type YourImaiCopy } from '@/i18n/your-imai-copy';
import { api, useQueryWithStatus } from '@/lib/convex';
import { yourImaiHref } from '@/lib/your-imai-routes';

import { usePageVisible } from './use-page-visible';
import {
  claimYourImaiNotification,
  yourImaiNotificationsPermitted,
  yourImaiTaskStatuses,
  yourImaiTurnOutcome,
  type YourImaiTurnMark,
  type YourImaiTurnNotice,
} from './your-imai-notifications';
import { resolveYourImaiPersona } from './your-imai-persona';
import { yourImaiAvatarIconPath } from '@/convex/your_imai/avatar_icon.helpers';

/**
 * Workspace-wide: at most one browser notification per IMAI turn, while the
 * member is not looking at the chat, and only when something needs them or
 * is finished (see yourImaiTurnOutcome). Clicking it opens the chat.
 */
export function YourImaiNotifier() {
  const params = useParams();
  const routeSlug = params?.orgSlug;
  const orgSlug = typeof routeSlug === 'string' ? routeSlug : null;
  return orgSlug ? <YourImaiOrgNotifier orgSlug={orgSlug} /> : null;
}

/** The notification's words: the turn's first line, or what happened. */
function noticeBody(
  notice: YourImaiTurnNotice,
  text: string | null,
  copy: YourImaiCopy
): string {
  if (text) return text;
  if (notice === 'needs_you') return copy.threadNeedsYou;
  if (notice === 'failed') return copy.replyFailed;
  return copy.newMessage;
}

function YourImaiOrgNotifier({ orgSlug }: { orgSlug: string }) {
  const copy = getYourImaiCopy(useLocale());
  const router = useRouter();
  const pathname = usePathname();
  const pageVisible = usePageVisible();
  const href = yourImaiHref(orgSlug);
  const viewingChat = pageVisible && pathname === href;
  const state = useQueryWithStatus(api.your_imai.feed.getYourImaiState, {
    orgSlug,
  }).data;
  // The same subscription the conversation's task cards read.
  const tasks = useQueryWithStatus(api.your_imai.children.listYourImaiThreads, {
    orgSlug,
  }).data;
  // Unset until the chat first loads: nothing notifies on load.
  const mark = useRef<YourImaiTurnMark | undefined>(undefined);
  // Read when a turn finishes; their own changes never notify.
  const context = useRef({ viewingChat, copy });
  useEffect(() => {
    context.current = { viewingChat, copy };
  });

  useEffect(() => {
    if (!state || tasks === undefined) return;
    const current = {
      status: state.status,
      awaitingAnswer: state.awaitingAnswer,
      latestImaiAt: state.latestImaiAt,
      latestUserTextId: state.latestUserTextId,
      tasks: yourImaiTaskStatuses(tasks),
    };
    const previous = mark.current;
    if (!previous) {
      mark.current = current;
      return;
    }
    const { finished, notice } = yourImaiTurnOutcome(previous, current);
    if (!finished) return;
    mark.current = current;
    const { viewingChat: viewing, copy: text } = context.current;
    if (
      !notice ||
      state.latestImaiAt === null ||
      state.unreadCount === 0 ||
      viewing ||
      !yourImaiNotificationsPermitted()
    )
      return;
    const persona = resolveYourImaiPersona(state.persona, text.imaiName);
    const body = noticeBody(notice, state.latestImaiText, text);
    void claimYourImaiNotification(state.orgThreadId, state.latestImaiAt).then(
      (claimed) => {
        if (!claimed) return;
        const notification = new Notification(persona.name, {
          body,
          tag: `your-imai:${orgSlug}`,
          // The same face its pushes show (the server's avatar route).
          icon: yourImaiAvatarIconPath(state.persona),
        });
        notification.onclick = () => {
          window.focus();
          router.push(href);
          notification.close();
        };
      }
    );
  }, [href, orgSlug, router, state, tasks]);

  return null;
}
